/**
 * DirectorInvolvementPanel — shows directors' other company involvements.
 */

import { Badge } from "@/ui/primitives/badge";
import { SourcePills } from "./SourcePill";

interface SourceCitation {
  doc: string;
  docId?: string;
  page?: number;
}

export interface DirectorOtherInvolvement {
  directorName: string;
  involvements: Array<{
    companyName: string;
    acn?: string;
    abn?: string;
    role: string;
    from?: string;
    to?: string;
    sources: SourceCitation[];
  }>;
  sources: SourceCitation[];
}

interface DirectorInvolvementPanelProps {
  directors: DirectorOtherInvolvement[];
  onViewDocument?: (docId: string) => void;
}

const ROLE_BADGE_COLOR: Record<
  string,
  "blue" | "purple" | "green" | "orange" | "gray"
> = {
  director: "blue",
  secretary: "green",
  shareholder: "purple",
  officer: "orange",
};

// Avatar with initials
function Avatar({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const hash = Array.from(name).reduce(
    (acc, ch) => ch.charCodeAt(0) + ((acc << 5) - acc),
    0,
  );
  const colors = [
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  ];
  return (
    <div
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${colors[Math.abs(hash) % colors.length]}`}
    >
      {initials}
    </div>
  );
}

export function DirectorInvolvementPanel({
  directors,
  onViewDocument,
}: DirectorInvolvementPanelProps) {
  const directorsWithInvolvements = directors.filter(
    (d) => d.involvements.length > 0,
  );

  if (directorsWithInvolvements.length === 0) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
      {directorsWithInvolvements.map((group, gi) => (
        <div key={gi} className={gi > 0 ? "border-t border-border" : ""}>
          <div className="px-5 py-4">
            <div className="mb-3 flex items-center gap-2.5">
              <Avatar name={group.directorName} />
              <span className="text-sm font-semibold text-foreground">
                {group.directorName}
              </span>
            </div>
            <div className="space-y-0">
              {group.involvements.map((inv, ci) => (
                <div
                  key={ci}
                  className={`flex items-start justify-between gap-3 py-2.5 ${ci > 0 ? "border-t border-border/50" : ""}`}
                >
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-foreground">
                      {inv.companyName}
                    </span>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {inv.acn ? `ACN ${inv.acn}` : ""}
                      {inv.acn && inv.abn ? " · " : ""}
                      {inv.abn ? `ABN ${inv.abn}` : ""}
                    </p>
                    <SourcePills
                      sources={inv.sources}
                      onViewDocument={onViewDocument}
                    />
                  </div>
                  <Badge
                    color={ROLE_BADGE_COLOR[inv.role] ?? "gray"}
                    variant="soft"
                    size="sm"
                    className="shrink-0 capitalize"
                  >
                    {inv.role.replace(/_/g, " ")}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
